import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Network, Code2, Rocket, Sparkles, Users, Grid3x3 } from 'lucide-react';
export function SolutionSlide() {
  const benefits = [{
    icon: <Network className="w-6 h-6" data-id="element-228" />,
    title: 'Open Ecosystem',
    desc: 'Seamlessly integrate with your existing CPMS and charger infrastructure. No vendor lock-in, complete flexibility.',
    color: 'accent',
    angle: -90
  }, {
    icon: <Code2 className="w-6 h-6" data-id="element-229" />,
    title: 'API-First Architecture',
    desc: 'Build custom solutions with our comprehensive API. Infinite possibilities for automation and integration.',
    color: 'purple',
    angle: -30
  }, {
    icon: <Rocket className="w-6 h-6" data-id="element-230" />,
    title: 'Launch in Days',
    desc: 'Get started immediately with our ready-made white-label application and pre-built components.',
    color: 'accent',
    angle: 30
  }, {
    icon: <Sparkles className="w-6 h-6" data-id="element-231" />,
    title: 'Smart Charging',
    desc: 'Deliver exceptional EV driver experiences with market-specific optimization features.',
    color: 'yellow',
    angle: 90
  }, {
    icon: <Users className="w-6 h-6" data-id="element-232" />,
    title: 'Customer Ownership',
    desc: 'Maintain direct customer relationships. You remain the primary interface for all services.',
    color: 'red',
    angle: 150
  }, {
    icon: <Grid3x3 className="w-6 h-6" data-id="element-233" />,
    title: 'Universal Compatibility',
    desc: 'One unified interface works across all charger brands. Simplify operations dramatically.',
    color: 'cyan',
    angle: 210
  }];
  const colorMap = {
    accent: 'border-[#00d8a8]/50 bg-[#00d8a8]/10 text-[#00d8a8]',
    purple: 'border-purple-500/50 bg-purple-500/10 text-purple-400',
    yellow: 'border-yellow-500/50 bg-yellow-500/10 text-yellow-400',
    red: 'border-red-500/50 bg-red-500/10 text-red-400',
    cyan: 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
  };
  const centerX = 300;
  const centerY = 300;
  const radius = 260;
  return <div className="h-full w-full flex flex-col md:flex-row items-start md:items-center px-4 md:px-16 bg-[#11166b] gap-8 md:gap-12 overflow-y-auto md:overflow-hidden py-16 md:py-0" data-id="element-234">
      {/* Left side - Text content */}
      <motion.div initial={{
      opacity: 0,
      x: -50
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.8
    }} className="flex-1 max-w-xl z-10 w-full mt-4 md:mt-0" data-id="element-235">
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 md:mb-6 leading-tight" data-id="element-236">
          Our Smart Charging <br data-id="element-237" />
          <span className="text-[#00d8a8]" data-id="element-238">Ecosystem for the Home</span>
        </h2>

        <p className="text-base md:text-xl text-slate-300 leading-relaxed mb-8 md:mb-0" data-id="element-239">
          A complete ecosystem designed to make your charging operations smarter
          and more efficient, while significantly reducing operational risk and
          vendor dependency.
        </p>
      </motion.div>

      {/* Right side - Ecosystem visualization */}
      <div className="flex-1 relative flex items-center justify-center w-full" data-id="element-240">
        {/* Mobile View: Vertical Stack */}
        <div className="md:hidden flex flex-col gap-6 w-full pb-24" data-id="element-241">
          {benefits.map((benefit, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + idx * 0.1
        }} className={`p-4 rounded-xl border backdrop-blur-sm ${colorMap[benefit.color as keyof typeof colorMap]} shadow-xl w-full`} data-id="element-242">
              <div className="flex items-start gap-3 mb-2" data-id="element-243">
                <div className="p-2 rounded-lg bg-[#0d1155] border border-[#1a2080] flex-shrink-0" data-id="element-244">
                  {benefit.icon}
                </div>
                <h3 className="text-sm font-bold text-white leading-tight" data-id="element-245">
                  {benefit.title}
                </h3>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed" data-id="element-246">
                {benefit.desc}
              </p>
            </motion.div>)}
        </div>

        {/* Desktop View: Circular Ecosystem */}
        <div className="hidden md:block relative w-[650px] h-[650px]" data-id="element-247">
          {/* Central Core Hub */}
          <motion.div initial={{
          scale: 0,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} className="absolute z-20" style={{
          left: `${centerX}px`,
          top: `${centerY}px`
        }} data-id="element-248">
            <div className="w-28 h-28 -ml-14 -mt-14 bg-[#0d1155] border-4 border-[#00d8a8] rounded-full flex items-center justify-center shadow-[0_0_60px_rgba(0,216,168,0.4)]" data-id="element-249">
              <span className="text-white font-bold text-lg tracking-wider" data-id="element-250">
                CORE
              </span>
            </div>

            {/* Pulsing rings */}
            <motion.div className="absolute inset-0 border-2 border-[#00d8a8]/30 rounded-full" animate={{
            scale: [1, 1.4, 1],
            opacity: [0.5, 0, 0.5]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-251" />
          </motion.div>

          {/* Benefit Cards in circular arrangement */}
          {benefits.map((benefit, idx) => {
          const angleRad = benefit.angle * Math.PI / 180;
          const x = Math.cos(angleRad) * radius;
          const y = Math.sin(angleRad) * radius;
          const cardWidth = 200;
          const left = centerX + x - cardWidth / 2;
          const top = centerY + y - 70;
          return <Fragment key={idx} data-id="element-252">
                {/* Connection line to core */}
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 1,
              delay: 0.5 + idx * 0.1
            }} className="absolute pointer-events-none" style={{
              left: `${centerX}px`,
              top: `${centerY}px`,
              width: '2px',
              height: `${radius}px`,
              background: 'linear-gradient(to bottom, rgba(0, 216, 168, 0.3), transparent)',
              transformOrigin: 'top center',
              transform: `rotate(${benefit.angle}deg)`
            }} data-id="element-253" />

                {/* Benefit Card */}
                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.5 + idx * 0.1
            }} className="absolute" style={{
              left: `${left}px`,
              top: `${top}px`,
              width: `${cardWidth}px`
            }} data-id="element-254">
                  <motion.div animate={{
                y: [0, -6, 0]
              }} transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: idx * 0.5
              }} className={`p-4 rounded-xl border-2 backdrop-blur-sm ${colorMap[benefit.color as keyof typeof colorMap]} shadow-xl`} data-id="element-255">
                    <div className="flex items-start gap-3 mb-2" data-id="element-256">
                      <div className="p-2 rounded-lg bg-[#0d1155] border border-[#1a2080] flex-shrink-0" data-id="element-257">
                        {benefit.icon}
                      </div>
                      <h3 className="text-sm font-bold text-white leading-tight" data-id="element-258">
                        {benefit.title}
                      </h3>
                    </div>
                    <p className="text-xs text-slate-300 leading-relaxed" data-id="element-259">
                      {benefit.desc}
                    </p>
                  </motion.div>
                </motion.div>
              </Fragment>;
        })}
        </div>
      </div>
    </div>;
}